import { FileUploadProgress, FileStatus } from '../types';

/**
 * @description Calculates the status of a file based on its progress
 * @param fileProgress - The progress of the file
 * @param index - The index of the file
 * @returns The status of the file
 */
export const calculateFileStatus = (
  fileProgress: Record<number, FileUploadProgress>,
  index: number
): FileStatus => {
  const progress = fileProgress[index];
  if (!progress) {
    return { status: 'pending', progress: 0 };
  }
  return {
    status: progress.status,
    progress: progress.progress,
    error: progress.error,
  };
};

/**
 * @description Generates status text based on file status and progress
 * @param status - The status of the file
 * @param progress - The progress of the file
 * @param error - The error of the file
 * @param translate - The translation function
 * @returns The status text
 * @returns {
 *   statusText: string;
 * }
 */
export const generateStatusText = (
  status: 'pending' | 'uploading' | 'success' | 'error',
  progress: number,
  error: string | undefined,
  translate: (key: string) => string
): string => {
  switch (status) {
    case 'pending':
      return '';
    case 'uploading':
      return `${Math.round(progress)}%`;
    case 'success':
      return translate('fileList.uploaded') || 'Uploaded';
    case 'error':
      return error || translate('fileList.error') || 'Error';
    default:
      return '';
  }
};

/**
 * @description Determines status appearance based on file status
 * @param status - The status of the file
 * @returns The status appearance
 * @returns {
 *   statusAppearance: 'success' | 'critical' | 'subdued' | undefined;
 * }
 */
export const getStatusAppearance = (
  status: 'pending' | 'uploading' | 'success' | 'error'
): 'success' | 'critical' | 'subdued' | undefined => {
  switch (status) {
    case 'success':
      return 'success';
    case 'error':
      return 'critical';
    case 'uploading':
      return undefined;
    default:
      return 'subdued';
  }
};

/**
 * @description Determines if a file can be removed based on upload state
 * @param isUploading - Whether the upload is in progress
 * @param status - The status of the file
 * @returns Whether the file can be removed
 * @returns {
 *   canRemove: boolean;
 * }
 */
export const canRemoveFile = (
  isUploading: boolean,
  status: 'pending' | 'uploading' | 'success' | 'error'
): boolean => {
  return !isUploading && status !== 'uploading';
};

/**
 * @description Calculates if required photos have been uploaded
 * @param filesCount - The number of files
 * @param totalPhotosRequired - The total number of photos required
 * @returns Whether the required photos have been uploaded
 * @returns {
 *   areRequiredPhotosUploaded: boolean;
 * }
 */
export const areRequiredPhotosUploaded = (
  filesCount: number,
  totalPhotosRequired: number | null
): boolean => {
  if (!totalPhotosRequired) return false;
  return filesCount >= totalPhotosRequired;
};

/**
 * @description Calculates if more files can be uploaded
 * @param filesCount - The number of files
 * @param totalPhotosRequired - The total number of photos required
 * @returns Whether more files can be uploaded
 * @returns {
 *   canUploadMoreFiles: boolean;
 * }
 */
export const canUploadMoreFiles = (
  filesCount: number,
  totalPhotosRequired: number | null
): boolean => {
  if (!totalPhotosRequired) return true;
  return filesCount < totalPhotosRequired;
};

/**
 * @description Generates success message text based on upload results
 * @param uploadResultsCount - The number of upload results
 * @param translate - The translation function
 * @returns The success message text
 * @returns {
 *   successMessage: string;
 * }
 */
export const generateSuccessMessage = (
  uploadResultsCount: number,
  translate: (key: string, options?: any) => string
): string => {
  if (uploadResultsCount === 1) {
    return translate('success.uploadComplete', {
      count: uploadResultsCount,
    });
  }
  return translate('success.uploadCompletePlural', {
    count: uploadResultsCount,
  });
};

/**
 * @description Generates uploading button text with progress
 * @param uploadProgress - The progress of the upload
 * @param translate - The translation function
 * @returns The uploading button text
 * @returns {
 *   uploadingButtonText: string;
 * }
 */
export const generateUploadingText = (
  uploadProgress: number,
  translate: (key: string, options?: any) => string
): string => {
  return translate('modal.uploading', {
    progress: uploadProgress.toFixed(0),
  });
};

/**
 * @description Generates all photos uploaded message
 * @param totalPhotosRequired - The total number of photos required
 * @returns The all photos uploaded message
 * @returns {
 *   allPhotosUploadedMessage: string;
 * }
 */
export const generateAllPhotosUploadedMessage = (
  totalPhotosRequired: number | null
): string => {
  if (!totalPhotosRequired) return '';
  return `You have uploaded all ${totalPhotosRequired} required photos. You can now submit your order.`;
};